import { useState } from "react";
import type { ReactElement } from "react";
import type { InvoiceFromOrderPreview } from "@kundeninfo/api-client";
import { FrageFuss, Modal } from "@kundeninfo/ui";
import { Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { INVOICE_FROM_ORDER_WEB_TEXT as T } from "./invoiceFromOrderTexts.js";

/**
 * DIE RÜCKFRAGE vor „Rechnung erzeugen“ an einem Auftrag mit offenen Zahlplan-Posten (Spec 2026-11-02 „Rechnung erzeugen
 * an Aufträgen mit Zahlplan“, Oberfläche; Festlegung P11).
 *
 * **Klein und gestapelt** nach Hausregel „Bestätigung vor Datenänderung“: `Modal frage` (art="frage", mittig, 480,
 * `unter="stapel"`, strikt, mit Kreuz; am Telefon als Blatt von unten, das entscheidet das Element). Kreuz und Escape
 * gehen den Weg von „Abbrechen“. Sie ist zugleich die Bestätigung vor der Datenänderung und ersetzt in diesem Fall die
 * allgemeine Frage „Rechnung erzeugen?“ (Entscheid 682) — eine Frage je Klick.
 *
 * **Drei Wege** im Fuss nach Entscheid 117 (`FrageFuss`, wie `bestaetigen({ alternative })`): (a) „Rechnung erzeugen“ als
 * Hauptknopf mit dem Anfangsfokus, (b) „Stattdessen vorschlagen“ links (nur mit `onPropose`, also nur am aktiven
 * Auftrag), (c) „Abbrechen“. Schreibtisch [b] … [c] [a], am Telefon a, b, c untereinander.
 *
 * **Rumpf**, Schlechtestes oben: erst die Notiz des Aufrufers (etwa nach einer 422) und die Hinweise des gewählten Jahres
 * (schon abgerechnet, schon in einem Entwurf), dann der Satz, dann — nur bei mehr als einem Jahr — die Jahreswahl.
 * Gerechnet wird hier nichts: Zahlen und Vorwahl kommen aus der Vorschau des Servers. Hinweise, Satz und Wahl stehen als
 * unmittelbare Kinder im Dialog: Abstand und Absatzrand setzt me-dialog `art="frage"` selbst (W-6).
 */

export type InvoiceFromOrderQuestionProps = {
  /** Die Vorschau des Servers — mindestens ein Jahr (ohne Jahre fragt der Aufrufer nicht). */
  preview: InvoiceFromOrderPreview;
  /** Ein Satz über allem, etwa nach einer 422 „Entscheidung fehlt“ oder „inzwischen vergeben“. */
  notice: string | null;
  /** Ein Schreibweg läuft: alle Wege gesperrt, auch Kreuz und Escape. */
  busy: boolean;
  /** (a) mit dem Beginn des gewählten Leistungsjahres. */
  onCreate: (periodStart: string) => void;
  /** (b) — ohne Angabe fehlt der Knopf (Auftrag nicht aktiv). */
  onPropose: (() => void) | undefined;
  /** (c) „Abbrechen“, Kreuz und Escape. */
  onCancel: () => void;
};

export function InvoiceFromOrderQuestion(p: InvoiceFromOrderQuestionProps): ReactElement {
  const { periods } = p.preview;
  const vorwahl = p.preview.defaultPeriodStart ?? periods[0]?.periodStart ?? "";
  const [wahl, setWahl] = useState(vorwahl);
  /* Nach einem Neuladen der Vorschau (422) kann das gewählte Jahr fehlen — dann gilt wieder die Vorwahl. */
  const gewaehlt = periods.find((x) => x.periodStart === wahl) ?? periods.find((x) => x.periodStart === vorwahl)
    ?? periods[0];
  const onPropose = p.onPropose;
  return (
    <Modal titel={T.title} frage onAbbrechen={p.onCancel} abbrechenGesperrt={p.busy}>
      {p.notice !== null && <Hinweis art="warnung">{p.notice}</Hinweis>}
      {gewaehlt !== undefined && gewaehlt.invoicedCount > 0 && <Hinweis art="warnung">{T.invoicedHint(gewaehlt)}</Hinweis>}
      {gewaehlt !== undefined && gewaehlt.inDraftCount > 0 && <Hinweis art="info">{T.inDraftHint(gewaehlt)}</Hinweis>}
      <p>
        {periods.length === 1 && gewaehlt !== undefined
          ? T.oneYear(gewaehlt, p.preview.invoiceNetAmount)
          : T.severalYears(periods.length, p.preview.invoiceNetAmount)}
      </p>
      {periods.length > 1 && (
        <Optionen beschriftung={T.periodLegend} wert={gewaehlt?.periodStart ?? ""} gesperrt={p.busy}
          onGeaendert={(d) => { if (d.wert) setWahl(d.wert); }}>
          {periods.map((x) => <option key={x.periodStart} value={x.periodStart}>{T.periodOption(x)}</option>)}
        </Optionen>
      )}
      <FrageFuss aktionText={T.create} gesperrt={p.busy} onAbbrechen={p.onCancel}
        {...(onPropose === undefined ? {} : { alternative: { text: T.propose, onClick: onPropose } })}
        onAktion={() => { if (gewaehlt !== undefined) p.onCreate(gewaehlt.periodStart); }} />
    </Modal>
  );
}
